import { useState, useEffect, useContext, useRef } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Stack,
  Chip,
  IconButton,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Divider,
  Alert,
} from "@mui/material";
import {
  IconPlugConnected,
  IconUserPlus,
  IconUpload,
  IconUsersGroup,
  IconTrash,
  IconEdit,
  IconBrandInstagram,
  IconBrandTiktok,
  IconBrandYoutube,
  IconBrandLinkedin,
  IconBrandFacebook,
  IconBrandX,
  IconX,
  IconDownload,
} from "@tabler/icons-react";
import * as XLSX from "xlsx";
import Button from "../components/Button";
import UserContext from "../contexts/UserContext";
import { fetchGet, fetchPost, fetchPut, fetchDelete } from "../util/helpers";

const PLATFORM_ICONS = {
  instagram: <IconBrandInstagram size={18} />,
  tiktok: <IconBrandTiktok size={18} />,
  youtube: <IconBrandYoutube size={18} />,
  linkedin: <IconBrandLinkedin size={18} />,
  facebook: <IconBrandFacebook size={18} />,
  twitter: <IconBrandX size={18} />,
};

const PLATFORM_COLORS = {
  instagram: { bg: "linear-gradient(135deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)", color: "#fff" },
  tiktok: { bg: "#010101", color: "#fff" },
  youtube: { bg: "#FF0000", color: "#fff" },
  linkedin: { bg: "#0A66C2", color: "#fff" },
  facebook: { bg: "#1877F2", color: "#fff" },
  twitter: { bg: "#000", color: "#fff" },
};

const ROLE_COLORS = {
  admin: { bg: "rgba(159, 37, 246, 0.1)", color: "#9F25F6", border: "1px solid rgba(159, 37, 246, 0.25)" },
  publisher: { bg: "rgba(77, 141, 255, 0.1)", color: "#4D8DFF", border: "1px solid rgba(77, 141, 255, 0.25)" },
  editor: { bg: "rgba(16, 185, 129, 0.1)", color: "#10B981", border: "1px solid rgba(16, 185, 129, 0.25)" },
  viewer: { bg: "rgba(107, 114, 128, 0.08)", color: "#6B7280", border: "1px solid rgba(107, 114, 128, 0.2)" },
};

const ROLES = ["admin", "publisher", "editor", "viewer"];

const sectionCardSx = {
  p: 0,
  borderRadius: 3,
  mb: 3,
  boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
  border: "1px solid rgba(0,0,0,0.06)",
  overflow: "hidden",
};

const sectionHeaderSx = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  px: 3,
  py: 2.5,
  borderBottom: "1px solid rgba(0,0,0,0.06)",
};

const tableHeadCellSx = {
  fontWeight: 600,
  fontSize: 12,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  color: "#6B7280",
  bgcolor: "rgba(159, 37, 246, 0.03)",
  borderBottom: "1px solid rgba(0,0,0,0.06)",
  py: 1.5,
};

const tableRowHoverSx = {
  transition: "background-color 0.15s ease",
  "&:hover": { bgcolor: "rgba(159, 37, 246, 0.02)" },
  "&:nth-of-type(even)": { bgcolor: "rgba(0,0,0,0.015)" },
  "&:nth-of-type(even):hover": { bgcolor: "rgba(159, 37, 246, 0.035)" },
};

const dialogPaperSx = {
  borderRadius: 3,
  overflow: "hidden",
  boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
};

export default function UsersGroupsPage() {
  const { user } = useContext(UserContext);
  const [channels, setChannels] = useState([]);
  const [members, setMembers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [addMemberOpen, setAddMemberOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [createGroupOpen, setCreateGroupOpen] = useState(false);
  const [newMember, setNewMember] = useState({ name: "", email: "", role: "viewer" });
  const [newGroup, setNewGroup] = useState({ name: "", channels: [], permissions: "viewer", members: [] });
  const [groupMemberEmail, setGroupMemberEmail] = useState("");
  const [groupMemberRole, setGroupMemberRole] = useState("viewer");
  const fileInputRef = useRef(null);

  const loadData = () => {
    fetchGet("/team/channels").then((res) => { if (res?.result) setChannels(res.result); }).catch(() => {});
    fetchGet("/team/members").then((res) => { if (res?.result) setMembers(res.result); }).catch(() => {});
    fetchGet("/team/groups").then((res) => { if (res?.result) setGroups(res.result); }).catch(() => {});
  };

  useEffect(() => { loadData(); }, []);

  const handleAddMember = async () => {
    if (!newMember.name || !newMember.email) return;
    try {
      await fetchPost("/team/members", newMember);
      setAddMemberOpen(false);
      setNewMember({ name: "", email: "", role: "viewer" });
      loadData();
    } catch (e) {
      console.error("Error adding member:", e);
    }
  };

  const handleUpdateMemberRole = async (email, role) => {
    try {
      await fetchPut(`/team/members/${encodeURIComponent(email)}`, { role });
      loadData();
    } catch (e) {
      console.error("Error updating member:", e);
    }
  };

  const handleRemoveMember = async (email) => {
    try {
      await fetchDelete(`/team/members/${encodeURIComponent(email)}`);
      loadData();
    } catch (e) {
      console.error("Error removing member:", e);
    }
  };

  const handleImportFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (evt) => {
      try {
        const text = evt.target.result;
        const lines = text.split("\n").filter((l) => l.trim());
        const membersToImport = lines.slice(1).map((line) => {
          const [name, email, permissions] = line.split(",").map((s) => s.trim());
          return { name, email, role: permissions?.toLowerCase() || "viewer" };
        }).filter((m) => m.name && m.email);
        await fetchPost("/team/members/import", { members: membersToImport });
        setImportOpen(false);
        loadData();
      } catch (err) {
        console.error("Import error:", err);
      }
    };
    reader.readAsText(file);
  };

  const handleCreateGroup = async () => {
    if (!newGroup.name) return;
    try {
      await fetchPost("/team/groups", newGroup);
      setCreateGroupOpen(false);
      setNewGroup({ name: "", channels: [], permissions: "viewer", members: [] });
      loadData();
    } catch (e) {
      console.error("Error creating group:", e);
    }
  };

  const handleDownloadGroupReport = async (group) => {
    try {
      const res = await fetchGet(`/team/groups/${group.id}/report`);
      if (!res?.result) return;
      const r = res.result;
      const ws = XLSX.utils.aoa_to_sheet([
        ["Group Report", group.name],
        [],
        ["Metric", "Value"],
        ["# Posts Launched", r.postsLaunched],
        ["# Comments", r.commentCount],
        ["% Positive Comments", `${r.pctPositiveComments}%`],
        ["% Response Rate", `${r.responseRatePct}%`],
      ]);
      ws["!cols"] = [{ wch: 22 }, { wch: 18 }];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Report");
      XLSX.writeFile(wb, `group_report_${group.name.replace(/\s+/g, "_")}.xlsx`);
    } catch (e) {
      console.error("Error downloading group report:", e);
    }
  };

  const handleDeleteGroup = async (id) => {
    try {
      await fetchDelete(`/team/groups/${id}`);
      loadData();
    } catch (e) {
      console.error("Error deleting group:", e);
    }
  };

  const handleAddGroupMember = () => {
    if (!groupMemberEmail) return;
    setNewGroup((prev) => ({
      ...prev,
      members: [...prev.members, { email: groupMemberEmail, role: groupMemberRole }],
    }));
    setGroupMemberEmail("");
    setGroupMemberRole("viewer");
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, minHeight: "100vh", bgcolor: "#FAFAFA" }}>
      {/* Gradient Header Banner */}
      <Paper
        sx={{
          background: "linear-gradient(135deg, #7C3AED 0%, #9F25F6 40%, #C334EB 100%)",
          borderRadius: 3,
          px: 4,
          py: 4,
          mb: 4,
          boxShadow: "0 4px 20px rgba(159, 37, 246, 0.25)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: -40,
            right: -40,
            width: 200,
            height: 200,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.06)",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: -60,
            right: 120,
            width: 140,
            height: 140,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.04)",
          }}
        />
        <Typography variant="h4" sx={{ color: "#fff", fontWeight: 700, mb: 0.5, position: "relative", zIndex: 1 }}>
          Users & Groups
        </Typography>
        <Typography sx={{ color: "rgba(255,255,255,0.8)", fontSize: 15, position: "relative", zIndex: 1 }}>
          Manage your team, channels, and groups
        </Typography>
      </Paper>

      {/* Section 1: Connect Social Channels */}
      <Paper sx={sectionCardSx}>
        <Box sx={sectionHeaderSx}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box sx={{ width: 4, height: 28, borderRadius: 2, background: "linear-gradient(180deg, #7C3AED, #C334EB)" }} />
            <IconPlugConnected size={20} color="#9F25F6" />
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: 17 }}>Connect Social Channels</Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              style={{ borderRadius: 10, padding: "8px 18px", fontSize: 13, background: PLATFORM_COLORS.facebook.bg, color: PLATFORM_COLORS.facebook.color }}
              onClick={() => fetchGet("/oauth/facebook").then(res => window.open(res.url, "_blank"))}
            >
              <IconBrandFacebook size={16} style={{ marginRight: 6 }} /> Facebook / Instagram
            </Button>
            <Button
              style={{ borderRadius: 10, padding: "8px 18px", fontSize: 13, background: PLATFORM_COLORS.tiktok.bg, color: PLATFORM_COLORS.tiktok.color }}
              onClick={() => fetchGet("/oauth/tiktok").then(res => window.open(res.url, "_blank"))}
            >
              <IconBrandTiktok size={16} style={{ marginRight: 6 }} /> TikTok
            </Button>
            <Button
              style={{ borderRadius: 10, padding: "8px 18px", fontSize: 13, background: PLATFORM_COLORS.youtube.bg, color: PLATFORM_COLORS.youtube.color }}
              onClick={() => fetchGet("/oauth/youtube").then(res => window.open(res.url, "_blank"))}
            >
              <IconBrandYoutube size={16} style={{ marginRight: 6 }} /> YouTube
            </Button>
            <Button
              style={{ borderRadius: 10, padding: "8px 18px", fontSize: 13, background: PLATFORM_COLORS.linkedin.bg, color: PLATFORM_COLORS.linkedin.color }}
              onClick={() => fetchGet("/oauth/linkedin").then(res => window.open(res.url, "_blank")).catch(() => window.open("/oauth/linkedin", "_blank"))}
            >
              <IconBrandLinkedin size={16} style={{ marginRight: 6 }} /> LinkedIn
            </Button>
            <Button
              style={{ borderRadius: 10, padding: "8px 18px", fontSize: 13, background: PLATFORM_COLORS.twitter.bg, color: PLATFORM_COLORS.twitter.color }}
              onClick={() => fetchGet("/oauth/twitter").then(res => window.open(res.url, "_blank")).catch(() => window.open("/oauth/twitter", "_blank"))}
            >
              <IconBrandX size={16} style={{ marginRight: 6 }} /> X (Twitter)
            </Button>
          </Box>
        </Box>
        <TableContainer sx={{ px: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={tableHeadCellSx}>Platform</TableCell>
                <TableCell sx={tableHeadCellSx}>Channel Name</TableCell>
                <TableCell sx={tableHeadCellSx}>Status</TableCell>
                <TableCell sx={tableHeadCellSx}>Permissions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {channels.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 5, borderBottom: "none" }}>
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5 }}>
                      <Box
                        sx={{
                          width: 56,
                          height: 56,
                          borderRadius: "50%",
                          background: "linear-gradient(135deg, rgba(124,58,237,0.08), rgba(195,52,235,0.08))",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <IconPlugConnected size={28} stroke={1.5} color="#9F25F6" />
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: 14 }}>
                        No channels connected yet. Link your social accounts above to get started.
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
              {channels.map((ch) => (
                <TableRow key={ch.id} sx={tableRowHoverSx}>
                  <TableCell sx={{ borderBottom: "1px solid rgba(0,0,0,0.04)" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: "50%",
                          background: PLATFORM_COLORS[ch.platform]?.bg || "#eee",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: PLATFORM_COLORS[ch.platform]?.color || "#666",
                          flexShrink: 0,
                        }}
                      >
                        {PLATFORM_ICONS[ch.platform] || ch.platform}
                      </Box>
                      <Typography sx={{ fontSize: 14, fontWeight: 500, textTransform: "capitalize" }}>{ch.platform}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ borderBottom: "1px solid rgba(0,0,0,0.04)", fontWeight: 500 }}>{ch.name}</TableCell>
                  <TableCell sx={{ borderBottom: "1px solid rgba(0,0,0,0.04)" }}>
                    <Chip
                      label={ch.status || "Connected"}
                      size="small"
                      sx={{
                        bgcolor: "rgba(16, 185, 129, 0.1)",
                        color: "#059669",
                        border: "1px solid rgba(16, 185, 129, 0.2)",
                        fontWeight: 600,
                        fontSize: 12,
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ borderBottom: "1px solid rgba(0,0,0,0.04)" }}>
                    <Select
                      size="small"
                      value={ch.permissions || "admin"}
                      onChange={(e) => fetchPut(`/team/channels/${ch.id}/permissions`, { permissions: e.target.value }).then(loadData)}
                      sx={{ minWidth: 110, fontSize: 13, borderRadius: 2, "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(0,0,0,0.1)" } }}
                    >
                      {ROLES.map((r) => <MenuItem key={r} value={r} sx={{ textTransform: "capitalize", fontSize: 13 }}>{r}</MenuItem>)}
                    </Select>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Section 2: Team Members */}
      <Paper sx={sectionCardSx}>
        <Box sx={sectionHeaderSx}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box sx={{ width: 4, height: 28, borderRadius: 2, background: "linear-gradient(180deg, #7C3AED, #C334EB)" }} />
            <IconUserPlus size={20} color="#9F25F6" />
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: 17 }}>Team Members</Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button style={{ borderRadius: 10, padding: "8px 18px", fontSize: 13 }} gradient onClick={() => setAddMemberOpen(true)}>
              <IconUserPlus size={16} style={{ marginRight: 6 }} /> Add Team Member
            </Button>
            <Button
              style={{
                borderRadius: 10,
                padding: "8px 18px",
                fontSize: 13,
                background: "rgba(159, 37, 246, 0.06)",
                color: "#7C3AED",
                border: "1px solid rgba(159, 37, 246, 0.2)",
              }}
              onClick={() => setImportOpen(true)}
            >
              <IconUpload size={16} style={{ marginRight: 6 }} /> Import Team List
            </Button>
          </Box>
        </Box>
        <TableContainer sx={{ px: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={tableHeadCellSx}>Name</TableCell>
                <TableCell sx={tableHeadCellSx}>Email</TableCell>
                <TableCell sx={tableHeadCellSx}>Permission</TableCell>
                <TableCell sx={tableHeadCellSx} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {members.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 5, borderBottom: "none" }}>
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5 }}>
                      <Box
                        sx={{
                          width: 56,
                          height: 56,
                          borderRadius: "50%",
                          background: "linear-gradient(135deg, rgba(124,58,237,0.08), rgba(195,52,235,0.08))",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <IconUserPlus size={28} stroke={1.5} color="#9F25F6" />
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: 14 }}>
                        No team members yet. Invite your first teammate to collaborate.
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
              {members.map((m) => (
                <TableRow key={m.email} sx={tableRowHoverSx}>
                  <TableCell sx={{ borderBottom: "1px solid rgba(0,0,0,0.04)" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <Box
                        sx={{
                          width: 34,
                          height: 34,
                          borderRadius: "50%",
                          background: "linear-gradient(135deg, #7C3AED, #C334EB)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#fff",
                          fontWeight: 700,
                          fontSize: 14,
                          flexShrink: 0,
                        }}
                      >
                        {m.name?.charAt(0)?.toUpperCase() || "?"}
                      </Box>
                      <Typography sx={{ fontWeight: 500, fontSize: 14 }}>{m.name}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ borderBottom: "1px solid rgba(0,0,0,0.04)", color: "#6B7280", fontSize: 14 }}>{m.email}</TableCell>
                  <TableCell sx={{ borderBottom: "1px solid rgba(0,0,0,0.04)" }}>
                    <Select
                      size="small"
                      value={m.role || "viewer"}
                      onChange={(e) => handleUpdateMemberRole(m.email, e.target.value)}
                      sx={{
                        minWidth: 110,
                        fontSize: 13,
                        borderRadius: 2,
                        "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(0,0,0,0.1)" },
                      }}
                    >
                      {ROLES.map((r) => <MenuItem key={r} value={r} sx={{ textTransform: "capitalize", fontSize: 13 }}>{r}</MenuItem>)}
                    </Select>
                  </TableCell>
                  <TableCell align="right" sx={{ borderBottom: "1px solid rgba(0,0,0,0.04)" }}>
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveMember(m.email)}
                      sx={{
                        color: "#EF4444",
                        bgcolor: "rgba(239,68,68,0.06)",
                        "&:hover": { bgcolor: "rgba(239,68,68,0.12)" },
                        width: 32,
                        height: 32,
                      }}
                    >
                      <IconTrash size={15} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Section 3: Groups */}
      <Paper sx={sectionCardSx}>
        <Box sx={sectionHeaderSx}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box sx={{ width: 4, height: 28, borderRadius: 2, background: "linear-gradient(180deg, #7C3AED, #C334EB)" }} />
            <IconUsersGroup size={20} color="#9F25F6" />
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: 17 }}>Groups</Typography>
          </Box>
          <Button style={{ borderRadius: 10, padding: "8px 18px", fontSize: 13 }} gradient onClick={() => setCreateGroupOpen(true)}>
            <IconUsersGroup size={16} style={{ marginRight: 6 }} /> Create a Group
          </Button>
        </Box>
        <TableContainer sx={{ px: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={tableHeadCellSx}>Group Name</TableCell>
                <TableCell sx={tableHeadCellSx}>Channels</TableCell>
                <TableCell sx={tableHeadCellSx}>Members</TableCell>
                <TableCell sx={tableHeadCellSx}>Permissions</TableCell>
                <TableCell sx={tableHeadCellSx}>Report</TableCell>
                <TableCell sx={tableHeadCellSx} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groups.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 5, borderBottom: "none" }}>
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5 }}>
                      <Box
                        sx={{
                          width: 56,
                          height: 56,
                          borderRadius: "50%",
                          background: "linear-gradient(135deg, rgba(124,58,237,0.08), rgba(195,52,235,0.08))",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <IconUsersGroup size={28} stroke={1.5} color="#9F25F6" />
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: 14 }}>
                        No groups yet. Organize team members into groups for easier management.
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
              {groups.map((g) => (
                <TableRow key={g.id} sx={tableRowHoverSx}>
                  <TableCell sx={{ borderBottom: "1px solid rgba(0,0,0,0.04)" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <Box
                        sx={{
                          width: 34,
                          height: 34,
                          borderRadius: 2,
                          background: "linear-gradient(135deg, rgba(124,58,237,0.1), rgba(195,52,235,0.1))",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <IconUsersGroup size={18} color="#9F25F6" />
                      </Box>
                      <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{g.name}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ borderBottom: "1px solid rgba(0,0,0,0.04)" }}>
                    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                      {g.channels?.map((ch) => (
                        <Chip
                          key={ch}
                          label={ch}
                          size="small"
                          sx={{
                            bgcolor: "rgba(77, 141, 255, 0.08)",
                            color: "#4D8DFF",
                            border: "1px solid rgba(77, 141, 255, 0.15)",
                            fontWeight: 500,
                            fontSize: 12,
                          }}
                        />
                      ))}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ borderBottom: "1px solid rgba(0,0,0,0.04)" }}>
                    <Chip
                      label={`${g.members?.length || 0} members`}
                      size="small"
                      sx={{ bgcolor: "rgba(0,0,0,0.04)", fontWeight: 500, fontSize: 12 }}
                    />
                  </TableCell>
                  <TableCell sx={{ borderBottom: "1px solid rgba(0,0,0,0.04)" }}>
                    <Chip
                      label={g.permissions}
                      size="small"
                      sx={{
                        textTransform: "capitalize",
                        fontWeight: 600,
                        fontSize: 12,
                        bgcolor: ROLE_COLORS[g.permissions]?.bg || ROLE_COLORS.viewer.bg,
                        color: ROLE_COLORS[g.permissions]?.color || ROLE_COLORS.viewer.color,
                        border: ROLE_COLORS[g.permissions]?.border || ROLE_COLORS.viewer.border,
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ borderBottom: "1px solid rgba(0,0,0,0.04)" }}>
                    <Button
                      style={{
                        borderRadius: 10,
                        padding: "6px 14px",
                        fontSize: 12,
                        fontWeight: 600,
                        background: "linear-gradient(135deg, #EEF2FF, #E0E7FF)",
                        color: "#4338CA",
                        border: "1px solid rgba(99,102,241,0.15)",
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                      }}
                      onClick={() => handleDownloadGroupReport(g)}
                    >
                      <IconDownload size={14} /> Download
                    </Button>
                  </TableCell>
                  <TableCell align="right" sx={{ borderBottom: "1px solid rgba(0,0,0,0.04)" }}>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteGroup(g.id)}
                      sx={{
                        color: "#EF4444",
                        bgcolor: "rgba(239,68,68,0.06)",
                        "&:hover": { bgcolor: "rgba(239,68,68,0.12)" },
                        width: 32,
                        height: 32,
                      }}
                    >
                      <IconTrash size={15} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add Team Member Dialog */}
      <Dialog open={addMemberOpen} onClose={() => setAddMemberOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: dialogPaperSx }}>
        <Box sx={{ height: 4, background: "linear-gradient(90deg, #7C3AED, #C334EB)" }} />
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 2,
                background: "linear-gradient(135deg, rgba(124,58,237,0.1), rgba(195,52,235,0.1))",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <IconUserPlus size={20} color="#9F25F6" />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>Add Team Member</Typography>
          </Box>
          <IconButton onClick={() => setAddMemberOpen(false)} size="small" sx={{ color: "#9CA3AF" }}><IconX size={18} /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1.5 }}>
            <TextField fullWidth label="Name" value={newMember.name} onChange={(e) => setNewMember((p) => ({ ...p, name: e.target.value }))} sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
            <TextField fullWidth label="Email" type="email" value={newMember.email} onChange={(e) => setNewMember((p) => ({ ...p, email: e.target.value }))} sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
            <FormControl fullWidth>
              <InputLabel>Permission</InputLabel>
              <Select value={newMember.role} onChange={(e) => setNewMember((p) => ({ ...p, role: e.target.value }))} label="Permission" sx={{ borderRadius: 2 }}>
                {ROLES.map((r) => <MenuItem key={r} value={r} sx={{ textTransform: "capitalize" }}>{r}</MenuItem>)}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, pt: 1, gap: 1 }}>
          <Button style={{ borderRadius: 10, padding: "8px 22px", fontSize: 13 }} onClick={() => setAddMemberOpen(false)}>Cancel</Button>
          <Button style={{ borderRadius: 10, padding: "8px 22px", fontSize: 13 }} gradient onClick={handleAddMember}>Add Member</Button>
        </DialogActions>
      </Dialog>

      {/* Import Team Dialog */}
      <Dialog open={importOpen} onClose={() => setImportOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: dialogPaperSx }}>
        <Box sx={{ height: 4, background: "linear-gradient(90deg, #7C3AED, #C334EB)" }} />
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 2,
                background: "linear-gradient(135deg, rgba(124,58,237,0.1), rgba(195,52,235,0.1))",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <IconUpload size={20} color="#9F25F6" />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>Import Team List</Typography>
          </Box>
          <IconButton onClick={() => setImportOpen(false)} size="small" sx={{ color: "#9CA3AF" }}><IconX size={18} /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Alert
            severity="info"
            sx={{
              mb: 2.5,
              borderRadius: 2,
              bgcolor: "rgba(77, 141, 255, 0.06)",
              border: "1px solid rgba(77, 141, 255, 0.15)",
              "& .MuiAlert-icon": { color: "#4D8DFF" },
            }}
          >
            Upload a CSV spreadsheet with columns: <strong>Name, Email, Permissions</strong> (Admin, Publisher, Editor, Viewer)
          </Alert>
          <input ref={fileInputRef} type="file" hidden accept=".csv,.xlsx,.xls" onChange={handleImportFile} />
          <Box
            onClick={() => fileInputRef.current?.click()}
            sx={{
              border: "2px dashed rgba(159, 37, 246, 0.2)",
              borderRadius: 3,
              py: 4,
              textAlign: "center",
              cursor: "pointer",
              transition: "all 0.2s",
              "&:hover": { borderColor: "#9F25F6", bgcolor: "rgba(159, 37, 246, 0.02)" },
            }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: "50%",
                background: "linear-gradient(135deg, rgba(124,58,237,0.1), rgba(195,52,235,0.1))",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 1.5,
              }}
            >
              <IconUpload size={24} color="#9F25F6" />
            </Box>
            <Typography sx={{ fontWeight: 600, fontSize: 14, color: "#374151" }}>Click to choose a file</Typography>
            <Typography sx={{ fontSize: 12, color: "#9CA3AF", mt: 0.5 }}>CSV, XLSX or XLS</Typography>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Create Group Dialog */}
      <Dialog open={createGroupOpen} onClose={() => setCreateGroupOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: dialogPaperSx }}>
        <Box sx={{ height: 4, background: "linear-gradient(90deg, #7C3AED, #C334EB)" }} />
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 2,
                background: "linear-gradient(135deg, rgba(124,58,237,0.1), rgba(195,52,235,0.1))",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <IconUsersGroup size={20} color="#9F25F6" />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>Create a Group</Typography>
          </Box>
          <IconButton onClick={() => setCreateGroupOpen(false)} size="small" sx={{ color: "#9CA3AF" }}><IconX size={18} /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1.5 }}>
            <TextField
              fullWidth
              label="Group Name"
              value={newGroup.name}
              onChange={(e) => setNewGroup((p) => ({ ...p, name: e.target.value }))}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
            <FormControl fullWidth>
              <InputLabel>Social Channels</InputLabel>
              <Select
                multiple
                value={newGroup.channels}
                onChange={(e) => setNewGroup((p) => ({ ...p, channels: e.target.value }))}
                input={<OutlinedInput label="Social Channels" />}
                sx={{ borderRadius: 2 }}
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((val) => (
                      <Chip
                        key={val}
                        label={val}
                        size="small"
                        sx={{ bgcolor: "rgba(77, 141, 255, 0.08)", color: "#4D8DFF", fontWeight: 500 }}
                      />
                    ))}
                  </Box>
                )}
              >
                {channels.map((ch) => (
                  <MenuItem key={ch.id} value={ch.name || ch.id}>
                    <Checkbox checked={newGroup.channels.includes(ch.name || ch.id)} />
                    <ListItemText primary={ch.name || ch.platform} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Group Permissions</InputLabel>
              <Select
                value={newGroup.permissions}
                onChange={(e) => setNewGroup((p) => ({ ...p, permissions: e.target.value }))}
                label="Group Permissions"
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="publisher">Publisher</MenuItem>
                <MenuItem value="editor">Editor</MenuItem>
                <MenuItem value="viewer">Viewer</MenuItem>
              </Select>
            </FormControl>
            <Divider sx={{ borderColor: "rgba(0,0,0,0.06)" }} />
            <Typography sx={{ fontWeight: 600, fontSize: 14, color: "#374151" }}>Add Members</Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <TextField
                size="small"
                label="Email"
                value={groupMemberEmail}
                onChange={(e) => setGroupMemberEmail(e.target.value)}
                sx={{ flex: 1, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
              <Select
                size="small"
                value={groupMemberRole}
                onChange={(e) => setGroupMemberRole(e.target.value)}
                sx={{ minWidth: 110, borderRadius: 2 }}
              >
                <MenuItem value="publisher">Publisher</MenuItem>
                <MenuItem value="editor">Editor</MenuItem>
                <MenuItem value="viewer">Viewer</MenuItem>
              </Select>
              <Button style={{ borderRadius: 8, padding: "4px 14px", fontSize: 12 }} gradient onClick={handleAddGroupMember}>Add</Button>
            </Box>
            {newGroup.members.length > 0 && (
              <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                {newGroup.members.map((m, i) => (
                  <Chip
                    key={i}
                    label={`${m.email} (${m.role})`}
                    size="small"
                    sx={{
                      bgcolor: "rgba(159, 37, 246, 0.06)",
                      color: "#7C3AED",
                      border: "1px solid rgba(159, 37, 246, 0.15)",
                      fontWeight: 500,
                    }}
                    onDelete={() => setNewGroup((p) => ({ ...p, members: p.members.filter((_, idx) => idx !== i) }))}
                  />
                ))}
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, pt: 1, gap: 1 }}>
          <Button style={{ borderRadius: 10, padding: "8px 22px", fontSize: 13 }} onClick={() => setCreateGroupOpen(false)}>Cancel</Button>
          <Button style={{ borderRadius: 10, padding: "8px 22px", fontSize: 13 }} gradient onClick={handleCreateGroup}>Create Group</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
